import React, { useEffect, useState } from "react";
import HostNav from "../components/hostComponents/HostNav";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import request from "../utils/request";
import onboard from "../icons/onboard.svg";
import { CircularProgress } from "@mui/material";
import { getFlags, getHostProperties, getHostEarnings, getAllOrders } from "../store/thunks/hostActions";
import { buildPropertyEarningsMap } from "../utils/earningsAggregation";

// Enhanced Metric Card with Animations
function MetricCard({ title, value, icon, color = "primary", trend, subtitle, onClick }) {
  const colorConfigs = {
    primary: {
      iconBg: "bg-gradient-to-br from-primary-500 to-primary-600",
      iconShadow: "shadow-primary-500/25",
      value: "text-primary-700",
    },
    success: {
      iconBg: "bg-gradient-to-br from-green-500 to-emerald-600",
      iconShadow: "shadow-green-500/25",
      value: "text-green-700",
    },
    blue: {
      iconBg: "bg-gradient-to-br from-blue-500 to-indigo-600",
      iconShadow: "shadow-blue-500/25",
      value: "text-blue-700",
    },
    purple: {
      iconBg: "bg-gradient-to-br from-purple-500 to-violet-600",
      iconShadow: "shadow-purple-500/25",
      value: "text-purple-700",
    },
    orange: {
      iconBg: "bg-gradient-to-br from-amber-500 to-orange-600",
      iconShadow: "shadow-amber-500/25",
      value: "text-orange-700",
    }
  };

  const config = colorConfigs[color] ?? colorConfigs.primary;

  return (
    <div 
      className="relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-white/40 p-4 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 rounded-xl ${config.iconBg} shadow-lg ${config.iconShadow} group-hover:scale-110 transition-transform duration-200`}>
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon === "dollar" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />}
            {icon === "chart" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
            {icon === "home" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />}
            {icon === "trending" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />}
            {icon === "star" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z" />}
            {icon === "orders" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />}
          </svg>
        </div>
        {trend && (
          <div className="flex items-center space-x-1 px-2 py-1 bg-green-50 rounded-lg border border-green-200/60">
            <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
            <span className="text-xs font-semibold text-green-600">{trend}</span>
          </div>
        )}
      </div>
      
      <h3 className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">{title}</h3>
      <p className={`text-2xl font-bold ${config.value} mb-1`}>{value}</p>
      {subtitle && <p className="text-xs text-gray-400 font-medium">{subtitle}</p>}
    </div>
  );
}

// Enhanced Property Performance Card
function PropertyPerformanceCard({ property, earnings, index, totalProperties }) {
  const navigate = useNavigate();
  
  const propertyEarnings = earnings[property._id]?.earnings?.reduce((sum, amount) => sum + amount, 0) || 0;
  const orderCount = earnings[property._id]?.earnings?.length || 0;
  const avgOrderValue = orderCount > 0 ? propertyEarnings / orderCount : 0;

  // Dynamic gradient colors
  const gradients = [
    "from-blue-500 via-blue-600 to-indigo-700",
    "from-emerald-500 via-green-600 to-teal-700", 
    "from-orange-500 via-red-600 to-pink-700",
    "from-purple-500 via-violet-600 to-purple-700",
    "from-cyan-500 via-blue-600 to-indigo-700",
    "from-rose-500 via-pink-600 to-red-700"
  ];
  
  const gradient = gradients[index % gradients.length];

  return (
    <div
      className="group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl border border-white/30 overflow-hidden transition-all duration-500 hover:-translate-y-3 cursor-pointer"
      onClick={() => navigate(`/properties/${property._id}`)}
    >
      {/* Animated Gradient Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-5 group-hover:opacity-15 transition-opacity duration-500`}></div>
      
      {/* Property Header */}
      <div className={`relative h-56 bg-gradient-to-br ${gradient} overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-8 h-8 bg-white/30 rounded-full animate-pulse"></div>
          <div className="absolute top-8 right-8 w-4 h-4 bg-white/20 rounded-full animate-pulse delay-500"></div>
          <div className="absolute bottom-8 left-8 w-6 h-6 bg-white/25 rounded-full animate-pulse delay-1000"></div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <p className="text-white/90 font-semibold text-sm">Property #{index + 1}</p>
          </div>
        </div>
        
        {/* Property Info Overlay */}
        <div className="absolute bottom-6 left-6 right-6">
          <h4 className="text-white font-bold text-xl mb-2 drop-shadow-lg group-hover:text-white/90 transition-colors">
            {property.listingTitle || `${property.address?.city} Property`}
          </h4>
          <p className="text-white/90 text-sm drop-shadow-md">
            {property.address?.line1}, {property.address?.city}
          </p>
        </div>
        
        {/* Performance Badge */}
        <div className="absolute top-6 right-6">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl border border-white/30">
            <p className="text-xs font-semibold text-gray-600 mb-1">Total Earnings</p>
            <p className="text-xl font-bold text-green-600">${propertyEarnings.toFixed(2)}</p>
          </div>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-6 relative z-10">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-2">Orders</p>
            <p className="text-2xl font-bold text-gray-900">{orderCount}</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-2">Avg Value</p>
            <p className="text-2xl font-bold text-gray-900">${avgOrderValue.toFixed(2)}</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-2">Rank</p>
            <p className="text-2xl font-bold text-gray-900">#{index + 1}</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-gray-600">Active Property</span>
          </div>
          <Link
            to={`/properties/${property._id}`}
            className="text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors flex items-center space-x-2 group-hover:scale-105 transform duration-200"
          >
            <span>View Details</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Top Earning Properties Square Cards
function TopEarningProperties({ propertyEarningsMap, properties }) {
  const navigate = useNavigate();

  // Calculate earnings for each property and sort by total earnings
  const propertyEarningsData = properties
    ?.filter(p => !p.isDeleted)
    ?.map(property => {
      const totals = propertyEarningsMap?.[property._id] || {};
      const totalEarnings = totals.totalHostFee || 0;
      const orderCount = totals.orderCount || 0;
      return {
        ...property,
        totalEarnings,
        orderCount
      };
    })
    ?.filter(p => p.totalEarnings > 0) // Only show properties with earnings > 0
    ?.sort((a, b) => b.totalEarnings - a.totalEarnings) // Sort by earnings descending
    ?.slice(0, 3); // Take top 3

  // Keep cards in a single horizontal row and reduce size
  const cardSizeClasses = {
    1: "w-56 h-56",
    2: "w-48 h-48",
    3: "w-40 h-40 md:w-44 md:h-44"
  };

  const cardSizeClass = cardSizeClasses[propertyEarningsData?.length || 0] || "w-40 h-40";

  if (!propertyEarningsData || propertyEarningsData.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 text-center border border-white/40 shadow-sm">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/25">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">No Earnings Yet</h3>
        <p className="text-gray-600 mb-4">Property earnings will appear here once orders are completed</p>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-sm border border-white/40">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Top Earning Properties</h2>
        <Link
          to="/properties"
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-md hover:shadow-lg text-sm font-medium"
        >
          <span>View All</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="flex items-stretch justify-center gap-3 sm:gap-4 flex-nowrap overflow-x-auto pb-1">
        {propertyEarningsData.map((property, index) => {
          // Dynamic gradient colors for the squares
          const gradients = [
            "from-blue-500 via-blue-600 to-indigo-700",
            "from-emerald-500 via-green-600 to-teal-700",
            "from-orange-500 via-red-600 to-pink-700"
          ];

          const gradient = gradients[index % gradients.length];

          return (
            <div
              key={property._id}
              className={`group relative bg-gradient-to-br ${gradient} rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer shrink-0 ${cardSizeClass}`}
              onClick={() => navigate(`/properties/${property._id}`)}
            >
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-2.5 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <p className="text-white/90 font-semibold text-xs mb-1.5 truncate max-w-[130px]">
                    {property.listingTitle || `Property ${index + 1}`}
                  </p>
                  <div className="text-center">
                    <p className="text-white font-bold text-base">${property.totalEarnings.toFixed(2)}</p>
                    <p className="text-white/70 text-xs">{property.orderCount} order{property.orderCount !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Enhanced Earnings Chart with Real Data
function EarningsChart({ earnings, properties }) {
  // Calculate monthly earnings for the last 6 months
  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (5 - i));
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    
    // Calculate actual earnings for this month (simplified)
    let monthlyEarnings = 0;
    if (earnings) {
      for (const propertyId in earnings) {
        const propertyEarnings = earnings[propertyId].earnings || [];
        monthlyEarnings += propertyEarnings.reduce((sum, amount) => sum + amount, 0) * 0.15; // Rough monthly estimate
      }
    }
    
    return { 
      month: monthName, 
      amount: Math.round(monthlyEarnings / 6) + Math.random() * 200 
    };
  });

  const maxAmount = Math.max(...monthlyData.map(d => d.amount));

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-sm border border-white/40">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">Earnings Trend</h3>
          <p className="text-sm text-gray-500">Monthly performance</p>
        </div>
        <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200/60">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
          </svg>
          <span className="text-sm font-semibold text-green-700">+18.2%</span>
        </div>
      </div>
      
      <div className="flex items-end justify-between h-32 space-x-2">
        {monthlyData.map((data, index) => {
          const height = (data.amount / maxAmount) * 100;
          return (
            <div key={index} className="flex flex-col items-center flex-1 group">
              <div 
                className="w-full bg-gradient-to-t from-primary-500 via-primary-400 to-primary-300 rounded-t-xl transition-all duration-300 hover:from-primary-600 hover:via-primary-500 hover:to-primary-400 cursor-pointer group-hover:scale-105 transform duration-200 relative"
                style={{ height: `${height}%` }}
              >
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 rounded-t-xl h-full flex items-end justify-center">
                  <span className="text-xs font-bold text-white mb-1 drop-shadow-lg">${data.amount}</span>
                </div>
              </div>
              <span className="text-xs font-semibold text-gray-600 mt-2">{data.month}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Quick Actions Panel
function QuickActionsPanel({ hostInfo }) {
  const navigate = useNavigate();

  const actions = [
    {
      title: "Add Property",
      description: "List a new property",
      icon: "M12 6v6m0 0v6m0-6h6m-6 0H6",
      gradient: "from-blue-500 to-indigo-600",
      shadow: "shadow-blue-500/25",
      link: "/add-property"
    },
    {
      title: "View Orders",
      description: "Check all orders",
      icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
      gradient: "from-green-500 to-emerald-600",
      shadow: "shadow-green-500/25",
      link: "/order-list"
    },
    {
      title: "Manage Properties",
      description: "Edit properties",
      icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
      gradient: "from-purple-500 to-violet-600",
      shadow: "shadow-purple-500/25",
      link: "/properties"
    },
    {
      title: "Settings",
      description: "Account settings",
      icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      gradient: "from-amber-500 to-orange-600",
      shadow: "shadow-amber-500/25",
      link: `/settings/${hostInfo?._id ?? ""}`
    }
  ];

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/40">
      <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.link)}
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${action.gradient} p-3 text-white shadow-md ${action.shadow} hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5`}
          >
            <div className="relative z-10">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
                </svg>
              </div>
              <h4 className="font-bold text-xs mb-0.5">{action.title}</h4>
              <p className="text-white/70 text-[10px]">{action.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Main Earnings Dashboard
function EarningsDashboard({ hostInfo }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { properties, loading: propertiesLoading } = useSelector((state) => state.hostProperties);
  const { earnings, loading: earningsLoading } = useSelector((state) => state.hostEarnings);
  const { orders: allOrders, loading: allOrdersLoading } = useSelector((state) => state.orders);
  const [loading, setLoading] = useState(true);

  const propertyEarningsMap = buildPropertyEarningsMap({
    orders: allOrders,
    earningsByProperty: earnings,
  });

  // Calculate comprehensive metrics
  let totalEarnings = 0;
  let monthlyEarnings = 0;
  let totalOrders = 0;
  let avgOrderValue = 0;

  if (propertyEarningsMap && typeof propertyEarningsMap === "object") {
    for (const propertyId in propertyEarningsMap) {
      const summary = propertyEarningsMap[propertyId] || {};
      totalEarnings += summary.totalHostFee || 0;
      totalOrders += summary.orderCount || 0;
    }

    avgOrderValue = totalOrders > 0 ? totalEarnings / totalOrders : 0;
    monthlyEarnings = totalEarnings * 0.25; // Rough monthly estimate
  }

  useEffect(() => {
    if (hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      dispatch(getAllOrders(hostInfo._id));
      setLoading(false);
    }
  }, [dispatch, hostInfo]);

  if (loading || propertiesLoading || earningsLoading || allOrdersLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={80} sx={{ color: '#EF4949' }} />
          <p className="mt-6 text-gray-600 font-semibold text-lg">Loading earnings dashboard...</p>
        </div>
      </div>
    );
  }

  const activePropertiesList = properties?.filter(p => !p.isDeleted) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-auto">
      {/* Subtle Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-blue-500/5 to-primary-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Compact Earnings Stats Bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Earnings Overview</h2>
            <Link
              to="/host"
              className="flex items-center space-x-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <span>Back to Dashboard</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard 
              title="Total Earnings" 
              value={`$${totalEarnings.toFixed(2)}`} 
              icon="dollar"
              color="success"
              trend="+12.5%"
              subtitle="All time"
              onClick={() => navigate('/order-list')}
            />
            <MetricCard 
              title="Monthly Average" 
              value={`$${monthlyEarnings.toFixed(2)}`} 
              icon="chart"
              color="primary"
              trend="+8.2%"
              subtitle="This month"
            />
            <MetricCard 
              title="Total Orders" 
              value={totalOrders.toString()} 
              icon="orders"
              color="blue"
              trend="+15.3%"
              subtitle="Completed"
              onClick={() => navigate('/order-list')}
            />
            <MetricCard 
              title="Avg Order Value" 
              value={`$${avgOrderValue.toFixed(2)}`} 
              icon="star"
              color="purple"
              trend="+5.7%"
              subtitle="Per order"
            />
          </div>
        </div>

        {/* Content Grid - More Compact */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Top Earning Properties - Takes 3 columns */}
          <div className="xl:col-span-3">
            <TopEarningProperties propertyEarningsMap={propertyEarningsMap} properties={properties} />
          </div>
          <div className="space-y-6">
            <EarningsChart earnings={earnings} properties={properties} />
            <QuickActionsPanel hostInfo={hostInfo} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HostDashboard() {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const isOnBoarded = useSelector((state) => state.hostDetails.Info?.isOnBoarded);
  const [onBoardLoading, setOnBoardLoading] = useState(false);

  const handleClick = async () => {
    try {
      setOnBoardLoading(true);
      const { data } = await request.get("/stripe/get-onboarding-link");
      window.location.href = data.url;
    } catch (e) {
      setOnBoardLoading(false);
    }
  };

  useEffect(() => {
    if (hostInfo?._id) dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);

  return (
    <div className="flex h-screen w-full overflow-auto bg-gray-50">
      <div className="mx-auto flex w-full flex-col px-6 tablet:px-0">
        <HostNav dashboard={true} hideproperty={true} />
        {isOnBoarded ? (
          <EarningsDashboard hostInfo={hostInfo} />
        ) : (
          <div className="flex items-center justify-center py-20">
            <div className="w-full max-w-[600px] overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative p-10">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-primary-100 opacity-50" />
                  <div className="relative">
                    <img src={onboard} alt="Stripe onboarding" className="mx-auto h-[140px] w-auto" />
                    <h3 className="mt-8 text-2xl font-bold text-gray-900">Connect Stripe to get started</h3>
                    <p className="mt-4 text-gray-600 text-lg">
                      You will be able to add properties, receive payouts, and unlock the full Bellhopt experience once your account is connected.
                    </p>
                    <button
                      onClick={handleClick}
                      className="mt-8 flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-70 text-lg"
                      disabled={onBoardLoading}
                    >
                      {onBoardLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        "Link Stripe Account"
                      )}
                    </button>
                    <div className="mt-4 text-center text-sm text-gray-500">
                      Secure redirect to Stripe
                    </div>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">Start Earning</h4>
                      <p className="text-gray-600 mt-2">Connect your account to begin</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HostDashboard;